import { useState, useEffect, useCallback } from "react";
import { hasConsent } from "../lib/cookie-consent";

function getInitialTheme(): "light" | "dark" {
  const stored = localStorage.getItem("theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function useTheme({ forceLight = false }: { forceLight?: boolean } = {}) {
  const [theme, setTheme] = useState<"light" | "dark">(getInitialTheme);

  // Single writer for data-theme. `forceLight` pins it for surfaces that are
  // light by design (the landing) without overwriting the stored preference,
  // so the rest of the app keeps the theme the visitor chose.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", forceLight ? "light" : theme);
    if (!forceLight && hasConsent()) {
      localStorage.setItem("theme", theme);
    }
  }, [theme, forceLight]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      const stored = localStorage.getItem("theme");
      if (!stored) setTheme(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return { theme, toggle };
}
